<template>
    <template-content-view>
        <template #left>
            <div class="caption">
                {{ companies ? $tr("Организации") : $tr("Пользователи") }}
            </div>
        </template>
        <template #content>
            <table-view :controller="controller" :sortable="true" />
        </template>
    </template-content-view>
</template>

<script>
import TableSimpleValue from '@topomatic/tap/src/components/TableSimpleValue.vue';
import TableUserValue from '@topomatic/tap/src/components/TableUserValue.vue';
import TableCompanyValue from '@topomatic/tap/src/components/TableCompanyValue.vue';
import TableDateValue from '@topomatic/tap/src/components/TableDateValue.vue';
import TableView, {
    TableViewController,
} from '@topomatic/tap/src/components/TableView.vue';
import TemplateContentView from '@topomatic/tap/src/components/TemplateContentView.vue';
import Utils from '@topomatic/tap/src/utils';
import Dialog from '@topomatic/tap/src/dialog';
import DialogInput from '@topomatic/tap/src/components/DialogInput.vue';
import AppConsts from '../consts';

class AdminController extends TableViewController {
    constructor(owner, data) {
        super();
        this.owner = owner;
        this.data = data;
    }

    headers() {
        return [
            this.owner.$tr('Наименование'),
            this.owner.$tr('Создан'),
            this.owner.$tr('Размер хранилища'),
            this.owner.$tr('Использовано'),
            this.owner.$tr('Время жизни ревизий'),
            this.owner.$tr('Количество ревизий'),
            this.owner.$tr('Время жизни корзины'),
        ];
    }

    length() {
        const { data } = this;
        return data != undefined ? data.length : 0;
    }

    id(row) {
        const { data } = this;
        return data[row].id;
    }

    component(row, column) {
        switch (column) {
        case 0:
            return this.owner.companies ? TableCompanyValue : TableUserValue;
        case 1:
            return TableDateValue;
        default:
            return TableSimpleValue;
        }
    }

    value(row, column) {
        const { data } = this;
        const value = data[row];
        switch (column) {
        case 0:
            if (this.owner.companies) {
                return {
                    value: this.owner.$api.get_company(value.id),
                };
            }
            return {
                value: this.owner.$api.get_user(value.id),
            };

        case 1:
            return value.created;
        case 2:
            return Utils.sizeToString(value.capacity);
        case 3:
            return Utils.sizeToString(value.used);
        case 4:
            return Utils.secondsToString(value.revision_time);
        case 5:
            return value.revision_size;
        case 6:
            return {
                value: Utils.secondsToString(value.trash_time),
                actions: [{
                    value,
                    title: this.owner.$tr('Изменить размер хранилища'),
                    onclick: this.changeCapacity.bind(this),
                },
                {
                    value,
                    title: this.owner.$tr('Изменить время жизни ревизий'),
                    onclick: this.changeRevisionTime.bind(this),
                },
                {
                    value,
                    title: this.owner.$tr('Изменить количество ревизий'),
                    onclick: this.changeRevisionSize.bind(this),
                },
                {
                    value,
                    title: this.owner.$tr('Изменить время жизни корзины'),
                    onclick: this.changeTrashTime.bind(this),
                },
                ],
            };
        default:
            return value;
        }
    }

    sortable(/* column */) {
        return true;
    }

    sort(column, order) {
        const { data } = this;
        if (data != undefined) {
            const { companies } = this.owner;
            const api = this.owner.$api;
            const compareNames = function (a, b) {
                if (companies) {
                    const v1 = api.get_company(a.id);
                    const v2 = api.get_company(b.id);
                    if (Utils.companyToString(v1) < Utils.companyToString(v2)) {
                        return order;
                    }
                    return -order;
                }
                const v1 = api.get_user(a.id);
                const v2 = api.get_user(b.id);
                if (Utils.userToString(v1) < Utils.userToString(v2)) {
                    return order;
                }
                return -order;
            };
            if (column == 0) {
                data.sort(compareNames);
            } else if (column == 1) {
                data.sort((a, b) => {
                    const s1 = Utils.dateTimeToString(a.created);
                    const s2 = Utils.dateTimeToString(b.created);
                    if (s1 < s2) {
                        return order;
                    }
                    return -order;
                });
            } else {
                let key;
                switch (column) {
                case 2:
                    key = 'capacity';
                    break;
                case 3:
                    key = 'used';
                    break;
                case 4:
                    key = 'revision_time';
                    break;
                case 5:
                    key = 'revision_size';
                    break;
                case 6:
                    key = 'trash_time';
                    break;
                default:
                    key = '';
                    break;
                }
                if (key != undefined) {
                    data.sort((a, b) => {
                        const v1 = a[key];
                        const v2 = b[key];
                        if (v1 < v2) {
                            return order;
                        } if (v1 > v2) {
                            return -order;
                        }
                        return compareNames(a, b);
                    });
                }
            }
        }
    }

    changeCapacity(sender) {
        const payload = {
            title: this.owner.$tr('Размер хранилища'),
            placeholder: `${this.owner.$tr('Размер хранилища')}, ${this.owner.$tr('ГБ')}`,
            value: sender.value.capacity / (1024 * 1024 * 1024),
            focus: true,
            validate: (value) => (value || '').trim().length > 0,
        };
        Dialog(DialogInput, payload)
            .then(() => {
                const value = Math.round(parseFloat(payload.value) * 1024 * 1024 * 1024);
                if (value >= 0) {
                    this.owner.$api.changeCapacity(sender.value.id, value)
                        .then(() => {
                            sender.value.capacity = value;
                        });
                }
            });
    }

    changeRevisionTime(sender) {
        const payload = {
            title: this.owner.$tr('Время жизни ревизий'),
            placeholder: `${this.owner.$tr('Время жизни ревизий')}, ${this.owner.$tr('дн')}`,
            value: sender.value.revision_time / (60 * 60 * 24),
            focus: true,
            validate: (value) => (value || '').trim().length > 0,
        };
        Dialog(DialogInput, payload)
            .then(() => {
                const value = Math.round(parseFloat(payload.value) * 60 * 60 * 24);
                if (value >= 0) {
                    this.owner.$api.changeRevisionTime(sender.value.id, value)
                        .then(() => {
                            sender.value.revision_time = value;
                        });
                }
            });
    }

    changeRevisionSize(sender) {
        const payload = {
            title: this.owner.$tr('Количество ревизий'),
            placeholder: this.owner.$tr('количество ревизий'),
            value: sender.value.revision_size,
            focus: true,
            validate: (value) => (value || '').trim().length > 0,
        };
        Dialog(DialogInput, payload)
            .then(() => {
                const value = Math.round(parseFloat(payload.value));
                if (value >= 0) {
                    this.owner.$api.changeRevisionSize(sender.value.id, value)
                        .then(() => {
                            sender.value.revision_size = value;
                        });
                }
            });
    }

    changeTrashTime(sender) {
        const payload = {
            title: this.owner.$tr('Время жизни корзины'),
            placeholder: `${this.owner.$tr('Время жизни корзины')}, ${this.owner.$tr('дн')}`,
            value: sender.value.trash_time / (60 * 60 * 24),
            focus: true,
            validate: (value) => (value || '').trim().length > 0,
        };
        Dialog(DialogInput, payload)
            .then(() => {
                const value = Math.round(parseFloat(payload.value) * 60 * 60 * 24);
                if (value >= 0) {
                    this.owner.$api.changeTrashTime(sender.value.id, value)
                        .then(() => {
                            sender.value.trash_time = value;
                        });
                }
            });
    }
}

export default {
    name: 'AdminView',
    components: {
        TemplateContentView,
        TableView,
    },
    props: {
        task: String,
    },
    data() {
        return {
            data: [],
        };
    },
    computed: {
        companies() {
            return this.task == AppConsts.APP_TASK_COMPANIES;
        },
        users() {
            return this.task == AppConsts.APP_TASK_USERS;
        },
        controller() {
            return new AdminController(this, this.data);
        },
    },
    methods: {
        fetch_data() {
            this.data = [];
            if (this.companies) {
                this.$api.getAdminCompanies()
                    .then((data) => {
                        this.data = data.companies;
                    });
            } else {
                this.$api.getAdminUsers()
                    .then((data) => {
                        this.data = data.users;
                    });
            }
        },
    },
    created() {
        this.fetch_data();
    },
    watch: {
        task() {
            this.fetch_data();
        },
    },
};
</script>

<style scoped>
.caption {
    font-size: 1.75rem;
    align-self: center;
    padding-left: 16px;
}
</style>
